import { Injectable } from "@angular/core";
import { ApiServiceCall } from "../global.apiServicecall";
import { ApiUrl } from "../../constants/apiUrl";
import { JwtTokenService } from "../auth/jwt-token.service";
import { Observable, of, forkJoin } from "rxjs";
import { map, concatMap } from "rxjs/operators";
import { OperationResult } from "../../models/operation-result";
import { UserProfileModel } from "../../models/profile/user-profile-model";
import { Notify } from 'notiflix/build/notiflix-notify-aio';
import { HttpClient, HttpHeaders } from "@angular/common/http";

@Injectable()
export class UserProfileService {
    constructor(
        private serviceCall: ApiServiceCall,
        private jwtTokenservice: JwtTokenService,
        private http: HttpClient
    ) { }

    getUserProfile(): Observable<UserProfileModel> {
        if(localStorage.getItem('profile')) {
           const profileData = JSON.parse(localStorage.getItem('profile')!);
           return of(Object.assign(new UserProfileModel(), profileData));
        } else {
            let _url = ApiUrl.userProfile;
            return this.serviceCall.GET(_url, true, null).pipe(
                map((response: any) => {
                    if (!response.body) throw new Error('Response body is null');
                    let userProfile = new UserProfileModel();
                    userProfile.id = response.body.id;
                    userProfile.first_name = response.body.first_name;
                    userProfile.last_name = response.body.last_name;
                    userProfile.email = response.body.email;
                    userProfile.phone_number = response.body.phone_number;
                    userProfile.birth_date = response.body.birth_date;
                    userProfile.is_active = response.body.is_active;
                    userProfile.gender = response.body.gender;
                    userProfile.weight = response.body.weight;
                    userProfile.height = response.body.height;
                    userProfile.imc = response.body.imc;
                    userProfile.photo_path = response.body.photo_path;
                    userProfile.has_to_upload_photo = response.body.has_to_upload_photo;
                    userProfile.has_to_update_weight = response.body.has_to_update_weight;
                    userProfile.has_unseen_notes = response.body.has_unseen_notes;
                    userProfile.address = response.body.address;
                    userProfile.city = response.body.city;
                    userProfile.zip = response.body.zip;
                    userProfile.allergies = response.body.allergies;
                    userProfile.orders = response.body.orders;
                    userProfile.meals_users = response.body.meals_users;
                    localStorage.setItem('profile', JSON.stringify(userProfile));
                    return userProfile;
                })
            );
        }
        
    }

    getUserExpert(): Observable<OperationResult<any>> {
        let _url = ApiUrl.userExpert;
        return this.serviceCall.GET(_url, true, null);
    }

    getUserBiometrics(): Observable<OperationResult<any>> {
        let _url = ApiUrl.biometrics;
        return this.serviceCall.GET(_url, true, null);
    }

    getExpertNotes(expertId: number): Observable<OperationResult<any>> {
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.experts + '/' + expertId + '/notes' + '/' + id;
        return this.serviceCall.GET(_url, true, null);
    }

    getUserOrders(page:number, orderType?:string): Observable<OperationResult<any>> {
        const userId = this.jwtTokenservice.getId();
        let _url = ApiUrl.userOrders + '/' + userId + '?limit=10&page=' + page;
        if (orderType && orderType !== '') {
            _url += '&orderType=' + orderType;
        }
        return this.serviceCall.GET(_url, true, null);
    }

    getUserOrder(orderId:number): Observable<OperationResult<any>> {
        const userId = this.jwtTokenservice.getId();
        let _url = ApiUrl.userOrder + '/' + orderId;
        return this.serviceCall.GET(_url, true, null);
    }

    getOrderSummaryPriceBreakdown(order_id: number): Observable<OperationResult<any>> {
        let _url = ApiUrl.orders + '/price_summary_breakdown/' + order_id;
        return this.serviceCall.GET(_url, true, null);
    }

    getUserOrderById(order_id: number): Observable<OperationResult<any>> {
        let _url = ApiUrl.orders + '/' + order_id;
        return this.serviceCall.GET(_url, true, null);
    }   
    
    getOldMeals(order_id:string): Observable<OperationResult<any>> {
        let _url = ApiUrl.basrUrl + "user/get_old_meals/" + order_id;
        return this.serviceCall.GET(_url, true, null);
    }

    approveOrder(data:any): Observable<OperationResult<any>>{
        let _url = ApiUrl.orders + "/approve";
        return this.serviceCall.Post(_url, true, data);
    }

    getUserCreditCards(): Observable<OperationResult<any>> {
        const userId = this.jwtTokenservice.getId();
        let _url = ApiUrl.userCreditCards + '/' + userId;
        return this.serviceCall.GET(_url, true, null);
    }

    addUserCreditCard(data:any): Observable<OperationResult<any>>{
        const userId = this.jwtTokenservice.getId();
        let _url = ApiUrl.userCreditCards + '/' + userId + '/create';
        return this.serviceCall.Post(_url, true, data);
    }
    
    deleteUserCreditCard(data:any): Observable<OperationResult<any>>{
        const userId = this.jwtTokenservice.getId();
        let _url = ApiUrl.userCreditCards + '/' + userId + '/delete';
        return this.serviceCall.Post(_url, true, data);
    }
    
    setUserWeight(weight: number): Observable<OperationResult<any>> {
        let _url = ApiUrl.setWeight;
        return this.serviceCall.Post(_url, true, { weight: weight });
    }

    setUserIMC(imc: number): Observable<OperationResult<any>> {
        let _url = ApiUrl.setImc;
        return this.serviceCall.Post(_url, true, { imc: imc });
    }

    setProfile(data: any): Observable<OperationResult<any>> {
        let _url = ApiUrl.userProfile;
        return this.serviceCall.Patch(_url, true, data);
    }

    setAddress(address: any): Observable<OperationResult<any>> {
        let _url = ApiUrl.setAddress;
        return this.serviceCall.Patch(_url, true, address);
    }

    updateProfile(userProfile: UserProfileModel): Observable<any> {
        return this.setProfile({first_name: userProfile.first_name, last_name: userProfile.last_name, phone_number: userProfile.phone_number});
    }

    addUserAllergies(data: any): Observable<OperationResult<any>> {
        let _url = ApiUrl.userProfile + '/allergies';
        localStorage.removeItem('profile');
        return this.serviceCall.Post(_url, true, data);
    }

    submitExpertReview(review: any, expertId: number): void {
        let _url = ApiUrl.experts + '/' + expertId + '/reviews';
        this.serviceCall.Post(_url, true, review).subscribe({
            next: this.handleSuccessResponse.bind(this),
            error: this.handleError.bind(this)
        });
    }

    handleSuccessResponseProfile(response: any): void {
        localStorage.setItem('userName', response.body.first_name + " " + response.body.last_name);
        this.handleSuccessResponse();
    }

    handleSuccessResponse(): void {
        localStorage.removeItem('profile');
        this.getUserProfile();
        Notify.success('Operation successful', {
            position: 'right-top',
            timeout: 3000
        });
    }

    handleError(error: any): void {
        console.log(error.error.message);
        Notify.failure(error.error.message, {
            position: 'right-top',
            timeout: 3000
        });
    }

    getPhotos(): Observable<OperationResult<any>>{
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.biometrics + '/' + id + '/photos';
        return this.serviceCall.GET(_url, true, null);
    }

    getBloods(): Observable<OperationResult<any>>{
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.biometrics + '/' + id + '/bloods';
        return this.serviceCall.GET(_url, true, null);
    }

    getSports(): Observable<OperationResult<any>>{
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.sports;
        return this.serviceCall.GET(_url, true, null);
    }

    addUserSport(data:any): Observable<OperationResult<any>>{
        let _url = ApiUrl.sports + '/sport';
        return this.serviceCall.Post(_url, true, data);
    }

    getUserSports(): Observable<OperationResult<any>>{
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.sports + '/sport';
        return this.serviceCall.GET(_url, true, null);
    }

    getNotification(): Observable<OperationResult<any>>{
        let _url = ApiUrl.notifications;
        return this.serviceCall.GET(_url, true, null);
    }

    setNotificationRead(data:any): Observable<OperationResult<any>>{
        let _url = ApiUrl.notifications + "/set_read";
        return this.serviceCall.Post(_url, true, data);
    }

    setUserPicturePicture(file: File): Observable<any> {
        let headers = new HttpHeaders();

        var jsonWebToken = localStorage.getItem('token');
        var id: string = localStorage.getItem('id') == null ? '' : localStorage.getItem('id')!;

        if (jsonWebToken)
            headers = headers.set(
                'Authorization',
                `Bearer ${jsonWebToken}`
            );
        const formData = new FormData();
        formData.append('photo', file);
        formData.append('user_id', id.toString());

        let _url = ApiUrl.setProfilePicture;
        return this.http.post(_url, formData, {
            headers: headers,
            observe: 'response' as 'body'
        });
    }

    setUserPicture(file: File, isPhoto: boolean): Observable<any> {
        let headers = new HttpHeaders();

        var jsonWebToken = localStorage.getItem('token');
        var id: string = localStorage.getItem('id') == null ? '' : localStorage.getItem('id')!;

        if (jsonWebToken)
            headers = headers.set(
                'Authorization',
                `Bearer ${jsonWebToken}`
            );
        const formData = new FormData();

        if (isPhoto)
            formData.append('photo', file);
        else
            formData.append('blood', file);

        formData.append('user_id', id.toString());

        let _url = '';
        if (isPhoto)
            _url = ApiUrl.setPicture;
        else
            _url = ApiUrl.setBlood;
        return this.http.post(_url, formData, {
            headers: headers,
            observe: 'response' as 'body'
        })/*.subscribe({
            next: this.handleSuccessResponse.bind(this),
            error: this.handleError.bind(this)
        })*/;
    }

    getCurrWeekOrder(order_id: string): Observable<OperationResult<any>>{
        const id = this.jwtTokenservice.getId();
        let _url = ApiUrl.orders + "/get_curr_week_order/" + order_id;
        return this.serviceCall.GET(_url, true, null);
    }

    replaceCurrWeekMeals(data:any, mealType: string = 'meal'): Observable<OperationResult<any>>{
        let _url = ApiUrl.orders + '/replace_curr_week_meals?meal_type=' + mealType;
        return this.serviceCall.Post(_url, true, data);
    }
    
    replaceOrderMealsFromWeek(data:any, mealType: string = 'meal'): Observable<OperationResult<any>>{
        let _url = ApiUrl.orders + '/replace_order_meals_from_week?meal_type=' + mealType;
        return this.serviceCall.Post(_url, true, data);
    }

    removeAddonsForWeek(orderId: number, week: number): Observable<OperationResult<any>> {
        const data = { order_id: orderId, week: week };
        let _url = ApiUrl.orders + '/remove_addons_for_week';
        return this.serviceCall.Post(_url, true, data);
    }
}